import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect } from "react";

export type AppSettingKey = "logo" | "app_icon" | "favicon";

export function useAppSettings() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["app_settings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("app_settings")
        .select("key, value");
      if (error) throw error;
      const map: Record<string, string> = {};
      data?.forEach((row: any) => {
        map[row.key] = row.value;
      });
      return map;
    },
    staleTime: 1000 * 60 * 60,      // 1 hour — settings change rarely
    gcTime:    1000 * 60 * 60 * 24, // keep 24 hours in memory
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

  // Realtime subscription for instant updates.
  // Unique channel name per hook instance — this hook is consumed by multiple
  // components (AuthContext + others) and Supabase Realtime rejects a second
  // subscribe() on a channel name that is already subscribed with:
  // "cannot add postgres_changes callbacks for realtime:<name> after subscribe()".
  useEffect(() => {
    const channelName = `app_settings_realtime_${Math.random().toString(36).slice(2)}`;
    const channel = supabase
      .channel(channelName)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "app_settings" },
        () => {
          queryClient.invalidateQueries({ queryKey: ["app_settings"] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  return query;
}

export function useAppSetting(key: AppSettingKey) {
  const { data, ...rest } = useAppSettings();
  return { data: data?.[key] ?? null, ...rest };
}

export function getBrandingUrl(path: string | null) {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  const { data } = supabase.storage.from("branding").getPublicUrl(path);
  const url = data?.publicUrl ?? null;
  // Trust CDN Cache-Control headers — no runtime cache-bust
  return url;
}

export function useInvalidateAppSettings() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ["app_settings"] });
}
